/**
 * The dock: where she lives when she is not being spoken to.
 *
 * Bottom-right, collapsed to a mark and a word. She does not surface urgent
 * work here on her own — that was a deliberate call. Urgent items lead the
 * welcome on arrival and answer the question when it is asked, and in between
 * she stays quiet, because an assistant that can interrupt mid-work-order is
 * one people learn to dismiss, including the time it mattered.
 *
 * The badge is not an exception to that. It counts her replies to things the
 * operator themselves did — a consequence after a transition — which is a
 * response, not an interruption.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import s from "./assistant.module.css";
import { AssistantMark } from "./AssistantMark";
import { useAssistant } from "./AssistantContext";
import { dueWords, isDue, ordered, parseWhen } from "./reminders";
import * as voice from "./voice";
import * as ears from "./listen";
import { Look, Sources, VoiceSettings } from "./SettingsPanels";
import { Button, Field, Input } from "../design/components";
import type { Draft, Turn, UrgentTurnItem } from "./types";
import { t } from "../i18n";
import { voiceLogText } from "./listen";

const STATE_WORD: Record<string, string> = {
  idle: "ready",
  listening: "listening",
  thinking: "hmm…",
  speaking: "speaking",
};

type Tab = "chat" | "due" | "sources" | "look" | "voice" | "reminders";

export function AssistantDock() {
  const a = useAssistant();
  const [tab, setTab] = useState<Tab>("chat");
  const due = a.reminders.filter((r) => isDue(r)).length;

  /*
   * Away, or put away. Both come down to one small tab, because the operator
   * asked for her to leave after the greeting and only come back when she is
   * clicked or spoken to — and a thing that removes itself with no way back is
   * not a feature.
   *
   * The two states differ only in what bringing her back means: `hidden` is a
   * saved decision and has to be undone, `present` is just where she is now.
   */
  /* On a call she is the call view, full size; one Sky on screen, not two. */
  if (a.onCall) return null;

  if (a.prefs.hidden || !a.present) {
    return (
      <div className={s.dock} data-sky-dock="" data-sky="sky">
        <button
          className={`${s.unhide} ${a.waking ? s.unhideListening : ""}`}
          onClick={() => {
            if (a.prefs.hidden) a.setPrefs({ ...a.prefs, hidden: false });
            a.summon();
          }}
          aria-label={t("Show Sky")}
          title={
            a.waking
              ? t("Listening for her name — say “Sky”, or press to bring her back")
              : t("Bring the assistant back")
          }
        >
          {t("Sky")}
          {a.due.length > 0 && <span className={s.photoBadge}>{a.due.length}</span>}
          {a.waking && <span className={s.earDot} aria-hidden="true" />}
        </button>
      </div>
    );
  }

  return (
    <div className={s.dock} data-sky-dock="" data-sky="sky">
      {a.open && <Panel tab={tab} setTab={setTab} />}
      {!a.open && (
        /*
         * Her picture alone in the corner. No pill, no label: the operator asked
         * for the conversation to appear only on clicking her, so she has to be
         * the thing that is clicked.
         */
        <button
          className={s.photoLauncher}
          onClick={() => a.summon()}
          aria-label={a.prefs.quiet ? t("Open Sky — she is quiet") : t("Open Sky")}
          title={
            a.prefs.quiet ? t("Quiet — she answers when asked and says nothing otherwise") : t("Ask her something")
          }
        >
          <AssistantMark state={a.mark} size={64} variant={a.prefs.mark} image={a.prefs.markImage} />
          {a.unread + due > 0 && <span className={s.photoBadge}>{a.unread + due}</span>}
          {a.prefs.quiet && <span className={s.photoQuiet}>{t("quiet")}</span>}
        </button>
      )}
    </div>
  );
}

function Panel({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  const a = useAssistant();
  const [text, setText] = useState("");
  const logRef = useRef<HTMLDivElement>(null);
  const dueReminders = a.reminders.filter((r) => isDue(r)).length;

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [a.turns.length, tab]);

  const TABS: { id: Tab; label: string }[] = [
    { id: "chat", label: t("Talk") },
    { id: "due", label: a.due.length ? t("Due {n}", { n: a.due.length }) : t("Due") },
    { id: "reminders", label: dueReminders ? t("Reminders {n}", { n: dueReminders }) : t("Reminders") },
    { id: "sources", label: t("Sources") },
    { id: "look", label: t("Look") },
    { id: "voice", label: t("Voice") },
  ];

  return (
    <section className={`${s.panel} ${a.leaving ? s.panelLeaving : ""}`} aria-label={t("Sky")} data-sky-dock="" data-sky="sky">
      <header className={s.head}>
        <AssistantMark
          state={a.mark}
          size={26}
          variant={a.prefs.mark}
          image={a.prefs.markImage}
          onClick={a.dismiss}
          title={t("Collapse the conversation")}
        />
        <div>
          <div className={s.headName}>{t("SKY")}</div>
          <div className={s.headState} aria-live="polite">
            {a.speaking
              ? t("reading aloud")
              : a.conversing && !a.listening
                ? t("in conversation")
                : t(STATE_WORD[a.mark])}
            {a.screen && a.mark === "idle" && !a.speaking
              ? ` · ${t("reading {title}", { title: a.screen.title })}`
              : ""}
          </div>
        </div>
        <span className={s.headSpacer} />
        {a.speaking && (
          <Button variant="ghost" size="sm" onClick={a.stopSpeaking}>
            {t("Stop")}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => a.setPrefs({ ...a.prefs, quiet: !a.prefs.quiet })}
          aria-pressed={a.prefs.quiet}
          title={
            a.prefs.quiet
              ? t("She is quiet: still working, still writing to the log, just not speaking up")
              : t("Ask her to be quiet — she will answer when asked and say nothing otherwise")
          }
        >
          {a.prefs.quiet ? t("Quiet ✓") : t("Be quiet")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            a.dismiss();
            a.setPrefs({ ...a.prefs, hidden: true });
          }}
          aria-label={t("Hide Sky")}
          title={t("Put her away — a small tab brings her back")}
        >
          {t("Hide")}
        </Button>
        <Button variant="ghost" size="sm" onClick={a.dismiss} aria-label={t("Close Sky")}>
          {t("Close")}
        </Button>
      </header>

      <nav className={s.tabs}>
        {TABS.map((tb) => (
          <button
            key={tb.id}
            className={`${s.tab} ${tab === tb.id ? s.tabOn : ""}`}
            onClick={() => setTab(tb.id)}
            aria-current={tab === tb.id}
          >
            {tb.label}
          </button>
        ))}
      </nav>

      {tab === "chat" && (
        <div className={s.log} ref={logRef}>
          {a.turns.length === 0 && (
            <p className={s.footNote}>
              {t(
                "I am Sky. Ask about anything on this site — a work order, an alert, a document, a rule. Ask what is most urgent and she will lead with the worst of it. She reads the platform's records first, and whatever you have connected beyond them. She will lay out options; she will not pick one.",
              )}
            </p>
          )}
          {a.turns.map((tu) => (
            <TurnView key={tu.id} turn={tu} />
          ))}
        </div>
      )}
      {tab === "due" && <DueList />}
      {tab === "sources" && <Sources />}
      {tab === "look" && <Look />}
      {tab === "voice" && <VoiceSettings />}
      {tab === "reminders" && <Reminders />}

      {tab === "chat" && (
        <footer className={s.foot}>
          {/*
           * One status row, always there. It used to appear and vanish with
           * the microphone, and every appearance pushed the typing box down a
           * line and every disappearance pulled it back — the "box that will
           * not hold still". Its height is reserved whether or not it has
           * anything to say.
           */}
          <div
            className={`${s.status} ${a.listening || a.heard ? s.heard : ""} ${a.heard ? "" : s.heardIdle}`}
            aria-live="polite"
          >
            {a.listening || a.heard
              ? a.heard || t("Listening…")
              : a.micBlocked
                ? t(
                    "The microphone could not be opened here — type your question, or press the microphone to try again.",
                  )
                : a.conversing
                  ? t("Listening again in a moment — say “thanks” when you are done.")
                  : ""}
          </div>
          <form
            className={s.ask}
            onSubmit={(e) => {
              e.preventDefault();
              const q = text;
              setText("");
              void a.ask(q);
            }}
          >
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("Ask her something")}
              aria-label={t("Ask Sky")}
            />
            {ears.isSupported() && (
              <Button
                variant={a.listening ? "default" : "ghost"}
                size="sm"
                onClick={() => {
                  if (a.micBlocked) {
                    /* A press after a refusal is a retry, not a toggle. */
                    a.startListening();
                    return;
                  }
                  if (a.prefs.voice.handsFree) {
                    /* One press turns the ear off for good, rather than just
                       this turn — otherwise it reopens a second later and the
                       button reads as broken. */
                    a.endConversation();
                    a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, handsFree: false } });
                  } else {
                    a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, handsFree: true } });
                  }
                }}
                aria-pressed={a.prefs.voice.handsFree}
                aria-label={a.prefs.voice.handsFree ? t("Stop listening") : t("Listen for my voice")}
                title={
                  a.prefs.voice.handsFree
                    ? t("Listening — press to stop and type instead")
                    : t("Not listening — press to speak instead of typing")
                }
              >
                {a.prefs.voice.handsFree ? "🎤" : "🔇"}
              </Button>
            )}
            <Button type="submit" variant="primary" size="sm" disabled={!text.trim()}>
              {t("Ask")}
            </Button>
          </form>
          <span className={s.footNote}>
            {!ears.isSupported()
              ? t(
                  "This browser has no speech recognition, so instructions are typed. She suggests and prepares; every action is still yours to press.",
                )
              : a.prefs.voice.handsFree
                ? t(
                    "Just speak — she is listening and answers what she hears. Type instead if you would rather; the microphone button turns listening off.",
                  )
                : t("Listening is off: type your question. The microphone button turns it back on.")}
          </span>
          <CopyVoiceLog />
        </footer>
      )}
    </section>
  );
}

/** What her microphone did, copied for whoever is looking into why she did not hear. */
function CopyVoiceLog() {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={s.callLog}
      onClick={() => {
        void navigator.clipboard
          ?.writeText(voiceLogText())
          .then(() => setCopied(true))
          .catch(() => setCopied(false));
      }}
    >
      {copied ? t("Voice log copied") : t("Copy voice log")}
    </button>
  );
}

function TurnView({ turn }: { turn: Turn }) {
  const a = useAssistant();
  const isOperator = turn.role === "operator";

  return (
    <article className={`${s.turn} ${isOperator ? s.turnOperator : s.turnAssistant}`}>
      {turn.consequence ? (
        <div className={s.consequence}>
          <div className={s.consequenceTitle}>{t("What follows")}</div>
          <ul className={s.consequenceList}>
            {turn.text.split("\n").map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className={s.bubble}>{turn.text}</div>
      )}

      {turn.urgent?.map((u) => (
        <UrgentCard key={u.id} item={u} />
      ))}

      {turn.suggestions && turn.suggestions.length > 0 && (
        <div className={s.suggestions}>
          {turn.suggestions.map((sg) =>
            sg.ask ? (
              <button
                key={sg.id}
                type="button"
                className={`${s.suggestion} ${s.suggestionAsk}`}
                onClick={() => void a.ask(sg.ask!)}
              >
                <span className={s.suggestionLabel}>{sg.label}</span>
                {sg.because && <span className={s.suggestionTrade}>{sg.because}</span>}
              </button>
            ) : (
              <div key={sg.id} className={s.suggestion}>
                <span className={s.suggestionLabel}>{sg.label}</span>
                {sg.because && <span className={s.suggestionTrade}>{sg.because}</span>}
                {sg.tradeOff && <span className={s.suggestionTrade}>{sg.tradeOff}</span>}
              </div>
            ),
          )}
        </div>
      )}

      {turn.draft && <DraftCard draft={turn.draft} />}

      {turn.more && <MoreButton more={turn.more} />}

      {turn.sources && turn.sources.length > 0 && (
        <div className={s.sources}>
          {turn.sources.map((src, i) => (
            <span key={i} className={`${s.source} ${src.kind === "external" ? s.sourceExternal : ""}`}>
              {src.label}
              {src.detail ? ` · ${src.detail}` : ""}
            </span>
          ))}
        </div>
      )}

      {!isOperator && voice.isSupported() && (
        <Button
          className={s.readAloud}
          variant="ghost"
          size="sm"
          onClick={() =>
            a.readAloud(
              [turn.spoken ?? turn.text, ...(turn.urgent ?? []).map((u) => `${u.label}. ${u.because}`)].join(". "),
            )
          }
        >
          {t("Read aloud")}
        </Button>
      )}
    </article>
  );
}

/**
 * An urgent record and what could be done about it.
 *
 * The options are listed, never ranked and never defaulted. One of them styled
 * as primary would be a recommendation, and a recommendation about a runway is
 * a decision — which is the one thing she is not allowed to make.
 */
function UrgentCard({ item }: { item: UrgentTurnItem }) {
  const sev = /severity 5|^Critical/.test(item.because) ? s.sev5 : /severity 4|^High/.test(item.because) ? s.sev4 : s.sevP;
  return (
    <div className={`${s.urgent} ${sev}`}>
      <div className={s.urgentLabel}>{item.label}</div>
      <div className={s.urgentBecause}>{item.because}</div>
      <div className={s.urgentOptions}>
        {item.options.map((o) => (
          <div key={o.id} className={s.urgentOption}>
            <span className={s.suggestionLabel}>{o.label}</span>
            {o.tradeOff && <span className={s.suggestionTrade}>{o.tradeOff}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * "Pull the full report."
 *
 * She answers in four or five lines and keeps the rest behind this. It presses
 * once and then says so, because the second press would append the same
 * material again and the operator would be reading it twice looking for the
 * difference.
 */
function MoreButton({ more }: { more: { label: string; lines: string[] } }) {
  const a = useAssistant();
  const [pulled, setPulled] = useState(false);
  if (pulled) return <div className={s.draftNote}>{t("Full report below.")}</div>;
  return (
    <Button
      className={s.readAloud}
      variant="ghost"
      size="sm"
      onClick={() => {
        setPulled(true);
        a.expand(more.lines);
      }}
    >
      {more.label}
    </Button>
  );
}

/**
 * The never-decides rule, rendered.
 *
 * Two buttons and no default. There is deliberately no "apply" that runs on
 * mount, no auto-confirm after a timeout, and no primary styling that makes one
 * choice look like the expected one — the operator is choosing, so the screen
 * should not lean.
 */
export function DraftCard({ draft }: { draft: Draft }) {
  const [state, setState] = useState<"open" | "working" | "done" | "discarded">("open");
  const [error, setError] = useState<string | null>(null);

  if (state === "discarded") return <div className={s.draftNote}>{t("Draft discarded.")}</div>;
  if (state === "done")
    return <div className={s.draftNote}>{t("{title} — raised by you.", { title: draft.title })}</div>;

  return (
    <div className={s.draft}>
      <div className={s.draftKind}>
        {draft.kind} · {t("prepared, not raised")}
      </div>
      <div className={s.draftTitle}>{draft.title}</div>
      <ul className={s.draftList}>
        {draft.summary.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
      <div className={s.draftActions}>
        <Button
          size="sm"
          disabled={state === "working"}
          onClick={async () => {
            setState("working");
            setError(null);
            try {
              await draft.commit();
              setState("done");
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
              setState("open");
            }
          }}
        >
          {state === "working" ? t("Creating…") : draft.commitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setState("discarded")}>
          {t("Discard")}
        </Button>
      </div>
      {error && <div className={s.draftNote}>{t("Did not go through: {error}", { error })}</div>}
    </div>
  );
}

/* ── reminders ────────────────────────────────────────────────────────── */

function Reminders() {
  const a = useAssistant();
  const [text, setText] = useState("");
  const [when, setWhen] = useState("");
  const parsed = useMemo(() => (when.trim() ? parseWhen(when) : null), [when]);
  const list = ordered(a.reminders);

  return (
    <div className={s.settings}>
      <p className={s.settingsNote}>
        {t(
          "Notes to yourself, held in this browser under your sign-in. They do not follow you to another terminal and the platform never sees them — for anything that has to survive the shift, create a new work order instead.",
        )}
      </p>

      {list.length === 0 && <p className={s.settingsHint}>{t("Nothing set.")}</p>}

      {list.map((r) => (
        <div key={r.id} className={`${s.reminder} ${isDue(r) ? s.reminderDue : ""} ${r.doneAt ? s.reminderDone : ""}`}>
          <span className={s.reminderText}>{r.text}</span>
          <span className={s.reminderWhen}>
            {r.doneAt ? t("done") : dueWords(r)}
            {r.about ? ` · ${r.about.label}` : ""}
          </span>
          <div className={s.reminderRow}>
            {!r.doneAt && (
              <Button size="sm" variant="ghost" onClick={() => a.completeReminder(r.id)}>
                {t("Done")}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => a.removeReminder(r.id)}>
              {t("Remove")}
            </Button>
          </div>
        </div>
      ))}

      <form
        className={s.reminderForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (!text.trim() || !parsed) return;
          a.addReminder(text, parsed.dueAt);
          setText("");
          setWhen("");
        }}
      >
        <Field label="Remind me to">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("re-check A2 friction")} />
        </Field>
        <Field label="When">
          <Input
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            placeholder={t("in 2 hours · 14:30 · tomorrow")}
          />
        </Field>
        <span className={s.settingsHint}>
          {when.trim()
            ? parsed
              ? t("Understood as: {said}.", { said: parsed.said })
              : t("Not understood. Try “in 90 minutes”, “14:30”, “tomorrow”, or “end of shift”.")
            : t("“in 90 minutes”, “14:30”, “tomorrow”, “later”, “end of shift”.")}
        </span>
        <div>
          <Button type="submit" size="sm" variant="primary" disabled={!text.trim() || !parsed}>
            {t("Set reminder")}
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ── what is running out ──────────────────────────────────────────────── */

/**
 * The two buttons are the whole design.
 *
 * "Later" records the band it was pressed at, so the same thing warns again at
 * fifteen days, at seven, and once more when the date passes. "Stop" ends it.
 * Keeping them apart is what lets an operator quiet a notice they have in hand
 * without also silencing the one that matters in three weeks' time.
 */
function DueList() {
  const a = useAssistant();

  if (a.due.length === 0) {
    return (
      <div className={s.settings}>
        <p className={s.settingsNote}>
          {t(
            "Nothing is inside thirty days that you have not already seen. She checks certificates, document reviews and work that is due — only the ones your access lets you read — and warns at thirty days, fifteen, seven, and once more if the date passes.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div className={s.settings}>
      <p className={s.settingsNote}>
        {t(
          "Warned at thirty days, fifteen and seven, and again once the date passes. “Later” answers this warning; the next one still comes. “Stop” ends them for that item.",
        )}
      </p>
      {a.due.map((d) => (
        <div key={d.id} className={`${s.urgent} ${d.days < 0 ? s.sev5 : d.days <= 7 ? s.sev4 : s.sevP}`}>
          <div className={s.urgentLabel}>{d.label}</div>
          <div className={s.urgentBecause}>{d.detail}</div>
          <div className={s.reminderRow}>
            <Button size="sm" variant="ghost" onClick={() => a.ackDue(d.id)}>
              {t("Later")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => a.stopDue(d.id)} title={t("Never warn about this again")}>
              {t("Stop")}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
